/**
 * Плагин: live-bendpoints во время жеста, persist updateConnection на отпускании курсора.
 *
 * X6 vertices/segments держат жест в batch (add-vertex / move-vertex / move-segment)
 * и закрывают его на document mouseup — edge:mouseup при этом не приходит (stopPropagation).
 * Удаление по dblclick батча не открывает — persist сразу из change:vertices.
 * После группового переноса (рамка Selection, batch move-selection) принудительно шлём bendpoints.
 */
const LIVE_THROTTLE_MS = 80;
const VERTEX_BATCHES = ['add-vertex', 'move-vertex', 'move-segment'];

const sameBendpoints = (a, b) => {
	if (!a && !b) return true;
	if (!a || !b || a.length !== b.length) return false;
	return a.every((p, i) => p.x === b[i].x && p.y === b[i].y);
};

const normalizeBendpoints = points =>
	(Array.isArray(points) ? points : []).map(p => ({
		x: Math.round(Number(p?.x ?? p?.$x ?? 0)),
		y: Math.round(Number(p?.y ?? p?.$y ?? 0)),
	}));

export default {
	init(modeler, context) {
		const {graph, onPatch, onLive, onLock, viewIndex} = context;
		const VIEW_INDEX = viewIndex ?? 0;
		const pending = new Map();
		let liveTimer = null;
		let lastLiveAt = 0;

		const model = graph.model ?? graph.getModel?.();

		const isVertexGesture = () =>
			Boolean(model?.hasActiveBatch?.(VERTEX_BATCHES));

		const isUserVertexChange = args => {
			if (isVertexGesture()) return true;
			if (args?.options?.ui === true) return true;
			const id = args?.edge?.id ?? args?.cell?.id;
			return Boolean(id && pending.has(id));
		};

		const readBendpoints = edge => normalizeBendpoints(edge.getVertices?.() ?? []);

		const readStartBendpoints = (args, id) => {
			if (Array.isArray(args?.previous)) {
				return normalizeBendpoints(args.previous);
			}
			const committed = modeler._committedGeometry?.get(id);
			if (Array.isArray(committed?.bendpoints)) {
				return normalizeBendpoints(committed.bendpoints);
			}
			const viewElement = modeler._findViewElementById(id);
			const list = viewElement?.waypointsNode?.waypoints || viewElement?.waypoints?.waypoint || [];
			return normalizeBendpoints(list);
		};

		const sendLive = () => {
			if (typeof onLive !== 'function' || pending.size === 0) return;
			const viewId = modeler.getActiveViewId?.() ?? null;
			pending.forEach((state, id) => {
				onLive({
					objectId: id,
					kind: 'connection',
					viewIndex: VIEW_INDEX,
					viewId,
					bendpoints: state.current,
				});
			});
		};

		const scheduleLive = () => {
			const wait = LIVE_THROTTLE_MS - (Date.now() - lastLiveAt);
			if (wait <= 0) {
				lastLiveAt = Date.now();
				sendLive();
				return;
			}
			if (!liveTimer) {
				liveTimer = setTimeout(() => {
					liveTimer = null;
					lastLiveAt = Date.now();
					sendLive();
				}, wait);
			}
		};

		const pushWaypointsPatch = (edge, id, {persist = true, live = false, args} = {}) => {
			if (modeler._suppressConnectionPatchSync) return;
			const bendpoints = readBendpoints(edge);
			if (live) {
				const prev = pending.get(id);
				if (!prev) {
					pending.set(id, {start: readStartBendpoints(args, id), current: bendpoints});
					onLock?.({
						action: 'acquire',
						objectId: id,
						kind: 'connection',
						viewIndex: VIEW_INDEX,
						viewId: modeler.getActiveViewId?.() ?? null,
					});
				} else {
					prev.current = bendpoints;
				}
				modeler._updateConnectionWaypointsInModel(id, bendpoints);
				scheduleLive();
				return;
			}
			modeler._updateConnectionWaypointsInModel(id, bendpoints);
			if (!persist) return;
			onPatch({
				patches: [
					{
						op: 'updateConnection',
						viewIndex: VIEW_INDEX,
						identifier: id,
						bendpoints,
					},
				],
			});
		};

		const commitPending = () => {
			if (liveTimer) {
				clearTimeout(liveTimer);
				liveTimer = null;
			}
			if (pending.size === 0) return;
			const viewId = modeler.getActiveViewId?.() ?? null;
			pending.forEach((state, id) => {
				modeler._geometryGestureIds?.delete(id);
				if (sameBendpoints(state.start, state.current)) {
					onLock?.({
						action: 'release',
						objectId: id,
						kind: 'connection',
						viewIndex: VIEW_INDEX,
						viewId,
					});
					return;
				}
				onPatch({
					patches: [
						{
							op: 'updateConnection',
							viewIndex: VIEW_INDEX,
							identifier: id,
							bendpoints: state.current,
						},
					],
				});
			});
			pending.clear();
		};

		const endpointMovedWithSelection = (nodeId, selectedCells) => {
			if (!nodeId) return false;
			const cell = graph.getCellById?.(nodeId);
			if (!cell || typeof cell.isNode !== 'function' || !cell.isNode()) return false;
			return selectedCells.some(sel => {
				if (!sel || typeof sel.isNode !== 'function' || !sel.isNode()) return false;
				if (sel.id === cell.id) return true;
				return typeof cell.isDescendantOf === 'function' && cell.isDescendantOf(sel);
			});
		};

		const flushWaypointsAfterSelectionMove = () => {
			if (modeler._suppressConnectionPatchSync || modeler._applyingPatch) return;
			const selectedCells =
				typeof graph.getSelectedCells === 'function' ? graph.getSelectedCells() : [];
			if (!selectedCells.length) return;
			graph.getEdges?.()?.forEach(edge => {
				if (!edge || typeof edge.isEdge !== 'function' || !edge.isEdge()) return;
				const verts = edge.getVertices?.() ?? [];
				if (!verts.length) return;
				const id = edge.id;
				if (modeler._updatingConnectionVerticesFromModel === id) return;
				if (!modeler._findViewElementById(id)) return;
				const s = edge.getSourceCellId?.();
				const t = edge.getTargetCellId?.();
				if (
					!endpointMovedWithSelection(s, selectedCells) &&
					!endpointMovedWithSelection(t, selectedCells)
				) {
					return;
				}
				pushWaypointsPatch(edge, id);
			});
		};

		const onEdgeVerticesChange = args => {
			if (modeler._suppressConnectionPatchSync || modeler._applyingPatch) return;
			if (!isUserVertexChange(args)) return;
			const edge = args?.edge ?? args?.cell;
			if (!edge || typeof edge.isEdge !== 'function' || !edge.isEdge()) return;
			const id = edge.id;
			if (modeler._updatingConnectionVerticesFromModel === id) return;
			if (!modeler._findViewElementById(id)) return;
			if (modeler.isLockedByOther?.(id)) return;
			if (!modeler._geometryGestureIds) modeler._geometryGestureIds = new Set();
			modeler._geometryGestureIds.add(id);
			pushWaypointsPatch(edge, id, {persist: false, live: true, args});
			if (!isVertexGesture()) {
				commitPending();
			}
		};

		const onBatchStop = ({name}) => {
			if (VERTEX_BATCHES.includes(name)) {
				commitPending();
				return;
			}
			if (name !== 'move-selection') return;
			commitPending();
			flushWaypointsAfterSelectionMove();
		};

		graph.on('edge:change:vertices', onEdgeVerticesChange);
		graph.on('batch:stop', onBatchStop);

		return () => {
			if (liveTimer) clearTimeout(liveTimer);
			commitPending();
			graph.off('edge:change:vertices', onEdgeVerticesChange);
			graph.off('batch:stop', onBatchStop);
		};
	},
};
